import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Carousel from "../components/carousel";
import { usePlaylist, useCollection, GET_ITEMS } from "../hooks/useInventory";
import { GET_ARTICLE } from "../hooks/useArticle";
import { useMutation, useQuery } from "@apollo/client";
import useInView from "../hooks/useInView";
import { Tile } from "../components/views";
import Parse, { domToReact } from "html-react-parser";
import URI from "urijs";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { POST_LIST_OPTIN } from "../sessionContext";
import { Store } from "../store";
import useNotification from "../hooks/useNotification";
import { useTranslation } from "react-i18next";
import { findUrlForMedia } from "../utils";

const Block = ({ block }) => {
  const { config, isMobile } = Store.useState(s => s);
  const { addNotification } = useNotification();
  const { t } = useTranslation();

  const classes = "block ";

  if (!block) return null;

  switch (block.type) {
    case "text":
      return (
        <div className={classes + "text paragraph"} dangerouslySetInnerHTML={{ __html: block.data.content }} />
      );
    case "embed": {
      const replace = (domNode) => {
        if (domNode.name === "a" && domNode.attribs.href && !(new URI(domNode.attribs.href).is("absolute"))) {
          return <Link to={domNode.attribs.href}>{domToReact(domNode.children)}</Link>;
        } else domToReact(domNode);
      };
      return (
        <div className={classes + "embed"}>
          {Parse(block.data.html || "", { replace })}
        </div>
      );
    }
    case "separator":
      return (
        <div className={classes + "separator"}>
          <hr />
        </div>
      );
    case "carousel":
      return (
        <div className={classes + "images carousel"}>
          <Carousel block={block} />
        </div>
      );
    case "playlist":
      return <PlaylistBlock block={block} isMobile={isMobile} />;
    case "subscribe": {
      if (!block.data.listRef) return null;
      return <SubscribeBlock config={config} t={t} listRef={block.data.listRef} isMobile={isMobile} addNotification={addNotification} />;
    }
    case "collection":
      return <CollectionBlock block={block} t={t} isMobile={isMobile} />;
    case "article":
      return <ArticleBlock block={block} t={t} isMobile={isMobile} />;
    case "items":
      return <ItemsBlock block={block} t={t} isMobile={isMobile} />;
    case "image": {
      if (!block.data || !block.data.media) break;
      const link = block.data.to || block.data.link;
      const imageUrl = findUrlForMedia(block.data.media, block.data.format);
      if (link) {
        if (link.includes("http"))
          return (
            <a target="_blank" rel="noreferrer noopener" href={link} className={classes + "image"}>
              <LazyLoadImage src={imageUrl} />
            </a>
          );
        else
          return (
            <Link to={link} className="image">
              <LazyLoadImage src={imageUrl} />
            </Link>
          );
      } else
        return (
          <div className={"image"}>
            <LazyLoadImage src={imageUrl} />
          </div>
        );
    }
    default: return null;
  }
};

const ArticleBlock = ({ block }) => {
  const { data, loading } = useQuery(GET_ARTICLE, { variables: { id: block.data.articleId } });

  const article = data?.article;
  if (loading || !data) return null;

  const format = block.data.format;
  const image = findUrlForMedia(article.thumbnail, format);

  return <div className="block article">
    {image ? <Link to={article.handle}><img src={image} /></Link> : null}
    <Link to={article.handle}><h2>{article.title}</h2></Link>
  </div>;
};

const ItemsBlock = ({ block, isMobile }) => {
  const { data, loading } = useQuery(GET_ITEMS, { variables: { ids: block.data.entries } });

  const items = data?.items;
  if (loading) return null;
  else if (!data) return null;

  return <div className="block items">
    {block.data.title ? <h2 className="title">{block.data.title}</h2> : null}
    <div className="entries" style={{ gridTemplateColumns: `repeat(${isMobile ? 2 : block.data.entriesPerRow}, 1fr)` }}>
      {items.map((e, idx) => <Tile key={idx} item={e} />)}
    </div>
  </div>;
};

const PlaylistBlock = ({ block, isMobile }) => {
  const [ref, onScreen] = useInView();
  const { getPlaylist, loading, playlist } = usePlaylist({ id: block.data.id });

  useEffect(() => {
    if (onScreen) getPlaylist();
  }, [onScreen, getPlaylist]);

  return (
    <div ref={ref} className={`block playlist ${isMobile ? "mobile" : ""}`}>
      {playlist && !loading ? <div className='content'>
          <div className="header">
            {playlist?.link ? <Link to={playlist.link}><h2 className="title">{playlist.title}</h2></Link> : <h2 className="title">{playlist?.title}</h2>}
          </div>
          <div className="entries">
            {playlist?.entries?.map((e, i) => <div key={i} className="playlistEntry">
                <Tile item={e.item} />
                <p>{e.comments}</p>
              </div>
            )}
          </div>
        </div> : null }
    </div>
  );
};

const CollectionBlock = ({ block, isMobile, t }) => {
  const [ref, onScreen] = useInView();
  const variables = {
    id: block.data.id,
    randomise: !!block.data.randomise,
    limit: block.data.limit,
    sort: block.data.sort,
    order: block.data.order
  };

  const { getCollection, collection } = useCollection(variables);

  useEffect(() => {
    if (onScreen) getCollection();
  }, [onScreen, getCollection]);

  const link = `${collection?.collection?.handle}?sort=${block.data.sort}&order=${block.data.order}`;

  return (
    <div ref={ref} className={`block collection ${isMobile ? "mobile" : ""}`}>
      <div className="content">
        <div className="header">
          <Link className="title" to={link}>
            <h2>{collection?.collection?.title}</h2>
          </Link>
          <Link className="seeMore" to={link}>
            <button className="primary" type='button'>{t("seeMore")}</button>
          </Link>
        </div>
        <div className="entries">
          {collection?.page?.items?.map(e => (<Tile key={e.id} item={e} />))}
        </div>
      </div>
    </div>
  );
};

const SubscribeBlock = ({ addNotification, t, listRef, isMobile }) => {
  const [subscribeMessage, setSubscribeMessage] = useState(undefined);
  const [optin, { loading }] = useMutation(POST_LIST_OPTIN);

  const handleAddToNewsletter = async e => {
    const email = e.target.email.value;
    e.preventDefault();
    try {
      const { data } = await optin({ variables: { email, listRef } });
      setSubscribeMessage(data.listOptin);
    } catch (e) {
      addNotification({ ok: 0, message: e.toString() });
    }
  };

  return (
    <div className={`block subscribe ${isMobile ? "mobile" : ""}`}>
      <h2>{t("subscribe")}</h2>
      <form onSubmit={handleAddToNewsletter}>
        {!subscribeMessage ? (
          <div className="content">
            <input type="email" name="email" placeholder={t("enterEmail")} required />
            <button type="submit" className="primary" disabled={subscribeMessage || loading}>
              {t("subscribe")}
            </button>
          </div>
        ) : (
          <p>{subscribeMessage}</p>
        )}
      </form>
    </div>
  );
};

export default Block;
export { SubscribeBlock };
